import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [expiredEmail, setExpiredEmail] = useState(''); // email returned when token expired
  const [resendState, setResendState] = useState({ loading: false, sent: false, error: '' });
  const [manualEmail, setManualEmail] = useState('');
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    const verifyEmail = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/auth/verify-email?token=${token}`);
        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage(data.message);
          setVerifiedEmail(data.email);

          // Auto-logout any existing session first
          await signOut();
          
          // Clear all auth data to force fresh login
          localStorage.clear();
          
          // Redirect to login page with email pre-filled after 3 seconds
          setTimeout(() => {
            navigate(`/login?email=${encodeURIComponent(data.email)}&verified=true`);
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed');
          if (data.expired && data.email) {
            setExpiredEmail(data.email);
          }
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred during verification');
      }
    };

    verifyEmail();
  }, [token, navigate, signOut]);

  const handleResend = async (emailToResend) => {
    if (!emailToResend) return;
    setResendState({ loading: true, sent: false, error: '' });
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToResend })
      });
      const data = await response.json();
      if (data.success) {
        setResendState({ loading: false, sent: true, error: '' });
      } else if (data.already_verified) {
        setResendState({ loading: false, sent: false, error: 'This email is already verified. Please sign in.' });
      } else {
        setResendState({ loading: false, sent: false, error: data.message || 'Failed to resend' });
      }
    } catch (err) {
      setResendState({ loading: false, sent: false, error: 'Network error — please try again' });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed inset-0 w-full h-full object-cover"
        style={{ zIndex: 0, filter: 'brightness(0.4)' }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60" style={{ zIndex: 1 }} />

      {/* Content */}
      <div className="relative z-10 bg-slate-900/95 backdrop-blur-md p-12 rounded-lg max-w-2xl border-4 border-yellow-500/50 shadow-2xl text-center"
        style={{ boxShadow: '0 0 40px rgba(234, 179, 8, 0.3)' }}
      >
        {status === 'verifying' && (
          <>
            <div className="text-6xl mb-6 animate-pulse">☢️</div>
            <h1 className="text-3xl font-bold text-yellow-400 mb-4">Verifying Your Email...</h1>
            <p className="text-slate-300">Please wait while we confirm your commander credentials.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-6xl mb-6">✅ 🎉</div>
            <h1 className="text-3xl font-bold text-green-400 mb-4">Email Verified!</h1>
            <p className="text-slate-300 text-lg mb-3">{message}</p>
            
            {verifiedEmail && (
              <div className="bg-blue-500/10 border border-blue-400 rounded-lg p-4 mb-6">
                <p className="text-blue-200 text-sm">
                  ✉️ Verified: <strong className="text-blue-300">{verifiedEmail}</strong>
                </p>
              </div>
            )}
            
            <div className="bg-green-500/10 border-2 border-green-400 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-green-300 mb-3">🎁 Bonuses Unlocked:</h3>
              <ul className="text-slate-300 text-left space-y-2">
                <li>✅ Early access to new countries</li>
                <li>✅ Priority game updates</li>
                <li>✅ Exclusive event notifications</li>
                <li>✅ "Verified Commander" status</li>
              </ul>
            </div>

            <p className="text-yellow-200 text-sm mb-2">🔄 Clearing old sessions and redirecting to login...</p>
            <p className="text-slate-400 text-xs">You'll be redirected to login in 3 seconds</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-6xl mb-6">❌</div>
            <h1 className="text-3xl font-bold text-red-400 mb-4">Verification Failed</h1>
            <p className="text-slate-300 text-lg mb-6">{message}</p>

            {/* Resend flow */}
            {!resendState.sent && (
              <div className="bg-yellow-500/10 border-2 border-yellow-500/40 rounded-lg p-6 mb-6 text-left">
                <h3 className="text-lg font-bold text-yellow-300 mb-3 text-center">
                  📧 Need a fresh verification link?
                </h3>

                {expiredEmail ? (
                  <>
                    <p className="text-slate-300 text-sm mb-4 text-center">
                      We'll send a new link to <strong className="text-yellow-300">{expiredEmail}</strong>
                    </p>
                    <button
                      data-testid="resend-verification-btn"
                      onClick={() => handleResend(expiredEmail)}
                      disabled={resendState.loading}
                      className="w-full px-6 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-all border-2 border-yellow-400"
                      style={{ boxShadow: '0 0 20px rgba(234, 179, 8, 0.4)' }}
                    >
                      {resendState.loading ? '📡 Sending...' : '☢️ Resend Verification Email'}
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      data-testid="resend-verification-email-input"
                      type="email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="your-email@example.com"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 text-white rounded-lg mb-3 focus:outline-none focus:border-yellow-400"
                    />
                    <button
                      data-testid="resend-verification-btn"
                      onClick={() => handleResend(manualEmail)}
                      disabled={resendState.loading || !manualEmail}
                      className="w-full px-6 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-all border-2 border-yellow-400"
                      style={{ boxShadow: '0 0 20px rgba(234, 179, 8, 0.4)' }}
                    >
                      {resendState.loading ? '📡 Sending...' : '☢️ Resend Verification Email'}
                    </button>
                  </>
                )}

                {resendState.error && (
                  <p data-testid="resend-error" className="text-red-400 text-sm mt-3 text-center">
                    {resendState.error}
                  </p>
                )}
              </div>
            )}

            {resendState.sent && (
              <div data-testid="resend-success" className="bg-green-500/10 border-2 border-green-400 rounded-lg p-6 mb-6">
                <div className="text-4xl mb-2">✅</div>
                <h3 className="text-xl font-bold text-green-300 mb-2">New Verification Email Sent!</h3>
                <p className="text-slate-300 text-sm">
                  Check your inbox at <strong className="text-green-300">{expiredEmail || manualEmail}</strong>.
                  The new link is valid for <strong>24 hours</strong>.
                </p>
                <p className="text-orange-300 text-xs mt-3 font-semibold">
                  ⚠️ Don't see it? Check your <strong>Spam</strong> / <strong>Junk</strong> / <strong>Promotions</strong> folder — that's where it usually hides.
                </p>
              </div>
            )}
            
            <button
              data-testid="return-to-game-btn"
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-white rounded-lg font-bold text-lg transition-all border-2 border-yellow-400"
              style={{ boxShadow: '0 0 20px rgba(234, 179, 8, 0.5)' }}
            >
              Return to Game
            </button>
          </>
        )}
      </div>
    </div>
  );
}
